import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  hiddenField,
  sOption,
  hiddenNumber,
  hiddenCode ,
  paramsAdd,
  paramsEdit,
} from '../constants/form';
import { statusRequest } from '../constants/base';
import { envSetting } from '../constants/envSetting';
import { siteMap } from '../constants/routes';
import {
  fieldAttribute,
  isUndefinedNull,
  notUndefinedNull,
  trimLowerCase,
  fieldUniqueName
} from '../helpers/shared';
import { setCategoriesTierFields, setTierFields, setFieldsCategories } from '../helpers/tier';
import { parentConditionEdit } from '../helpers/edit';
import { getCondition } from '../helpers/conditions';
import { validateAssistantRule } from '../helpers/base';
import { getProductRequest } from '../redux/actions/user';
import { setNavigate, setLoader } from '../redux/actions/shared'
import { getBase, getAvailableAssistants } from '../redux/actions/base'
import {
  TIER_CATEGORY,
  TIER_SET_TIERS,
  TIER_CATEGORIES,
  TIER_VALUES_EDIT,
  TIER_CLEAR_CATEGORY,
  TIER_PARAMS,
  TIER_PARENTS
} from '../redux/types/tier';
import { USER_FILES } from '../redux/types/user';
import { BASE_ALL_ASSISTANTS } from '../redux/types/base';
import { isLendecyApp } from '../helpers/base';
import { TIER_SET_NEXT_TIER, TIER_LAST_TIER } from '../redux/types/tier';

/**
 * use to validate action in tier
 * @returns
 */
export const useValidateTier = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { actionTier, codeTier } = useParams();
  const { level } = useSelector(state => state.user);
  const { requestLanguage } = useSelector(state => state.base);
  const { action, code } = useSelector(state => state.tier);
  const staticData = useSelector(state => state.staticData);
  const tiers = staticData ? staticData[`data_${trimLowerCase(requestLanguage)}`]?.tier : undefined;

  useEffect(() => {
    if (notUndefinedNull(actionTier) && notUndefinedNull(codeTier)) {
      if (isUndefinedNull(action) && isUndefinedNull(code)) {
        dispatch({
          type: TIER_PARAMS,
          payload: { action: actionTier, code: codeTier }
        });
      }
      if (action !== actionTier || code !== codeTier) {
        dispatch({
          type: TIER_PARAMS,
          payload: { action: actionTier, code: codeTier }
        });
      }
    }
  }, [actionTier, codeTier]);

  useEffect(() => {
    if (trimLowerCase(actionTier) !== paramsEdit && trimLowerCase(
      actionTier) !== paramsAdd) {
      navigate(siteMap.homepage.path);
    }
  }, [actionTier, navigate]);

  useEffect(() => {
    const info = tiers?.find(elem => codeTier === elem.unique_name);
    if ((parseInt(level + 1) < parseInt(info?.verification_level) || parseInt(
      level) >= parseInt(info?.verification_level)) && trimLowerCase(
        actionTier) === paramsAdd) {
      navigate(siteMap.homepage.path);
    }
    if (parseInt(info?.verification_level) > parseInt(level) && trimLowerCase(
      actionTier) === paramsEdit) {
      navigate(siteMap.homepage.path);
    }
  }, [actionTier, codeTier, level, tiers, navigate]);
};

/**
 * Use to component module
 * @returns
 */
export const useModule = () => {
  const dispatch = useDispatch();
  const { codeTier } = useParams();
  const { tiers, tier, tierFieldState, tierCategories, lastTier } = useSelector(
    state => state.tier);
  const { data: personData } = useSelector(state => state.personData);
  const { requestLanguage, location, selectedAssistant, allAssistants } = useSelector(
    state => state.base);
  const { [`data_${trimLowerCase(requestLanguage)}`]: {
    assistant_rule: assistantRules } = {} } = useSelector(state => state.staticData);
  const [tierCode, setTierCode] = useState({});
  const [categoriesCode, setCategoriesCode] = useState([]);
  const [fieldStateCode, setFieldStateCode] = useState([]);

  useEffect(() => {
    if (isUndefinedNull(tierFieldState) && isUndefinedNull(
      tierCategories) && isUndefinedNull(tier)) {
      let dataTier = tiers?.[trimLowerCase(codeTier)];
      if (!dataTier) {
        dataTier = tiers?.[trimLowerCase(lastTier?.unique_name)];
      }
      if (dataTier) {
        setTierCode(dataTier.data);
        setCategoriesCode(dataTier.categories.data);
        setFieldStateCode(dataTier.fields);
        dispatch({
          type: TIER_CATEGORIES,
          payload: {
            tier: dataTier.data,
            // ? Here the categories are being set.
            tierCategories: dataTier.categories.data,
            tierFieldState: dataTier.fields,
            numberCategories: dataTier.categories.data.length
          }
        });
        const parents = [...new Set(Array.from(
          dataTier.fields).map(item => item.parent).filter(
            item => notUndefinedNull(item)))];
        dispatch({ type: TIER_PARENTS, payload: parents });
        dispatch({ type: USER_FILES, payload: { files: null } });
      }
    } else {
      setTierCode(tier);
      setCategoriesCode(tierCategories);
      setFieldStateCode(tierFieldState);
    }
  }, [codeTier, tiers]);

  useEffect(() => {
    const getAssistants = async () => {
      if (isUndefinedNull(allAssistants) && envSetting.listAllAssistants === "true") {
        // dispatch(setLoader(true));
        const data = {
          country_code: location,
          user_id: personData.id,
          by_distance: true
        };
        const response = await dispatch(getAvailableAssistants(data));
        dispatch({ type: BASE_ALL_ASSISTANTS, payload: response });
        // dispatch(setLoader(false));
      }
    };

    getAssistants();
  }, [allAssistants]);

  const needAssistant = validateAssistantRule(location, assistantRules);

  return {
    allAssistants,
    categoriesTier: categoriesCode,
    needAssistant,
    selectedAssistant,
    tier: tierCode,
    tierFieldState: fieldStateCode
  };
};

/**
 * Use to completed tier
 */
export const useCompletedForm = methods => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getValues, setValue } = methods;
  const { isNavigate, route } = useSelector(state => state.user);
  const { action, parents, tierFieldState, code, lastTier } = useSelector(state => state.tier);
  const { requestLanguage } = useSelector(state => state.base);
  const { field: fields } = useSelector(state => {
    const data = state.staticData[`data_${trimLowerCase(requestLanguage)}`];
    return data || {};
  });
  const tier = useSelector(state => state.tier) 
  const { code: currentTierCode  } = tier
  const timeForParentCondition = (action === paramsEdit) ? 10000 : 5000
  const [isParentConditionDone, setIsParentConditionDone] = useState(false)
  const { codeTier } = useParams()

  useEffect(() => {
    if (isNavigate) {
      dispatch(setNavigate(false));
      navigate(route);
    }
  }, [dispatch, isNavigate, navigate, route]);

  useEffect(() => {
    if ((action === paramsEdit || action === paramsAdd) || codeTier === 'rejected' || codeTier === 'expired' ) {
      // dispatch(setLoader(true));
      setTimeout(() => {
        dispatch(setLoader(true));
        if (parents.length > 0) {
          Array.from(parents).forEach(item => {
            const [field] = Array.from(tierFieldState).filter(
              elem => trimLowerCase(elem.id) === trimLowerCase(item));
            const data = (field?.storage_data_key) ? JSON.parse(
              field.storage_data_key) : null;
            if (field?.unique_name && data?.show !== 'false') {
              const uniqueName = `${field.unique_name}__id__${field.id}`;
              const element = fieldAttribute('name', uniqueName);
              if (element) {
                const value = trimLowerCase(getValues(element.name));
                if (value !== '' && notUndefinedNull(value) && value !== hiddenField && value !== hiddenNumber && value !== hiddenCode && field) {
                  parentConditionEdit(element, field, methods);
                }
              }
            }
          });

        }
      }, 1000);
      setTimeout(() => {
        // Function commented since it seems to perform the same function that parentConditionEdit already does.
        // caseEditAction(parents, methods);
        if (action === paramsEdit || action === paramsAdd) {
          // * Checking if any field in the group of the 'names to check' fields (those with 'storage_data_key.editing:[{'type':'check'}]') has a value.
          // If they are all empty, set them all to 'hiddenField'.
          const namesToCheck = [];
          const currentTierFields = tier?.tiers?.[currentTierCode]?.fields
          if (currentTierFields) {
            currentTierFields?.forEach(field => {
              const data = (field?.storage_data_key) ? JSON.parse(field.storage_data_key) : null;
              if (notUndefinedNull(data?.editing) && data?.editing[0]?.type === 'check') {
                namesToCheck.push(field.unique_name + '__id__' + field.id);
              }
            });
            namesToCheck.forEach(name => {
              const [group] = name.split('__');
              let groupFields = currentTierFields.filter(field => field.unique_name.includes(`${group}__`));
              groupFields = groupFields.map(field => `${field.unique_name}__id__${field.id}`);
              let groupFieldsValues = [];
              groupFields.forEach(groupField => {
                groupFieldsValues.push(getValues(groupField));
              });
              const areAllEmpty = !groupFieldsValues.some(groupFieldsValue => {
                return (
                  groupFieldsValue &&
                  groupFieldsValue !== hiddenCode &&
                  groupFieldsValue !== hiddenField &&
                  groupFieldsValue !== hiddenNumber &&
                  groupFieldsValue !== sOption &&
                  notUndefinedNull(groupFieldsValue)
                );
              });
              if (areAllEmpty) {
                groupFields.forEach(groupField => {
                  setValue(groupField, hiddenField);
                });
              }
            });
          }

        }
      }, 1000);

      setTimeout(() => {
        fields.filter(field => JSON.parse(field?.action)?.type === 'hide')
          // We will also check for the condition of the hide/show fields (those with an action.type === 'hide')
          .forEach(field => {
          const [parent] = fields.filter(innerField => innerField.id === field.parent)
          const parentElement = fieldUniqueName(parent.unique_name)
          const resultCondition = getCondition(
            JSON.parse(field.parent_condition),
            parentElement,
            field.data_type
          )
          if(resultCondition.isCondition){
            let uniqueName = `${field.unique_name}__id__${field.id}`
            setValue(uniqueName, hiddenField)
          }
        })
      }, 2000)
    }
  }, [action, parents]);

  useEffect(() => {
    // Calling parentConditionsEdit, after a delay
    if(action === paramsEdit || action === paramsAdd){
      setTimeout(() => {
        if (parents.length > 0) {
          Array.from([...parents]).forEach(item => {
            const [field] = Array.from(tierFieldState).filter(
              elem => trimLowerCase(elem.id) === trimLowerCase(item));
            const data = (field?.storage_data_key) ? JSON.parse(
              field.storage_data_key) : null;
            if (field?.unique_name && data?.show !== 'false') {
              const uniqueName = `${field.unique_name}__id__${field.id}`;
              const element = fieldAttribute('name', uniqueName);
              if (element) {
                const value = trimLowerCase(getValues(element.name));
                if (notUndefinedNull(value) && value !== hiddenField && value !== hiddenNumber && value !== hiddenCode && field) {
                  parentConditionEdit(element, field, methods);
                }
              }
            }
          });

        }
        setIsParentConditionDone(true)
      }, timeForParentCondition);
    }
  }, [action, parents])

  useEffect(() => {
    // Just some extra time added before hiding the Loader, giving all components enough time to update
    let extraTime = 2000
    if (notUndefinedNull(code)) {
      if (code === 'advanced') {
        if (isParentConditionDone) {
          setTimeout(() => {
            dispatch(setLoader(false))
          }, extraTime);
        } 
      } else {
        setTimeout(() => {
          // ? Hide Loader in all cases EXCEPT 
          // ? if its a 'speed_form' on action 'add'
          if (!(action === paramsAdd) && (lastTier?.unique_name === 'speed_form')) {
            dispatch(setLoader(false))
          }
        }, extraTime);
      }
    }
  }, [isParentConditionDone, code])

};

/**
 * Use to get info current and last tiers
 * @returns
 */
export const useInfoTiers = () => {
  const { requestLanguage } = useSelector(state => state.base);
  const { action, lastTier, nextTier, tier } = useSelector(state => state.tier);
  const { appOrigin } = useSelector(state => state.user);
  const [infoTier, setInfoTier] = useState({});
  const { tier: tiers } = useSelector(state => {
    const data = state.staticData[`data_${trimLowerCase(requestLanguage)}`];
    return data || {};
  });

  /*
  * Sets 'infoTier'. State used to determine the nextTier to redirect to, after Completed. 
  */
  useEffect(() => {
    let targetLevel;
    const activeTiers = tiers?.filter(tier => tier.active);

    if (action === paramsAdd || lastTier?.unique_name === 'speed_form') {
      targetLevel = parseInt(nextTier?.verification_level) + 1;
    } else {
      targetLevel = parseInt(tier?.verification_level) + 1;
    }

    const matchingTiers = activeTiers?.filter(
      elem => parseInt(elem.verification_level) === targetLevel
    );

    let tierToSet;

    if (matchingTiers?.length === 1) {
      tierToSet = matchingTiers[0];
    
    } else if (matchingTiers?.length > 1) {
      const isLendecy = isLendecyApp();
      if (isLendecy) {
        tierToSet = tiers?.find(elem => elem.unique_name === 'slim_advanced');
      } else {
        tierToSet = tiers?.find(elem => elem.unique_name === 'advanced');
      }
    } else {
      tierToSet = {}; 
    }

    setInfoTier(tierToSet);

  }, [tiers, action, nextTier, tier, appOrigin]);

  return { nextTier: infoTier, lastTier };
};

/**
 * use to get categories and fields for tier
 */
export const useTiers = () => {
  const dispatch = useDispatch();
  const { requestLanguage } = useSelector(state => state.base);
  const {
    field_category: categories,
    tier: allTiers,
    tier_field: tiersFields,
    field: fields
  } = useSelector(state => state.staticData[`data_${trimLowerCase(
    requestLanguage)}`]) || {};
  const { tiers, lastTier } = useSelector(state => state.tier);
  const location = useLocation()

  useEffect(() => {
    if (isUndefinedNull(tiers)) {
      const response = {};
      Array.from(allTiers).forEach(item => {
        if (item.regular && item.active) {
          const uniqueName = trimLowerCase(item.unique_name);
          const tierCategoriesFields = setCategoriesTierFields(
            categories, item, tiersFields);
          const tierFields = setTierFields(fields, item, tiersFields);
          const fieldsCategories = setFieldsCategories(
            tierCategoriesFields, tierFields, item);
          response[uniqueName] = {};
          response[uniqueName]['fields'] = tierFields;
          response[uniqueName]['data'] = item;
          response[uniqueName]['categories'] = {};
          response[uniqueName]['categories']['data'] = tierCategoriesFields;
          response[uniqueName]['categories']['fields'] = fieldsCategories;
        }
      });
      dispatch({ type: TIER_SET_TIERS, payload: response });
    }
  }, [tiers, allTiers, categories, tiersFields, fields]);

  useEffect(() => {
    const callGetBase = async () => {
      await dispatch(getBase())
    }

    if (location.pathname === siteMap.homepage.path) {
      dispatch({ type: TIER_CLEAR_CATEGORY });
      dispatch({ type: USER_FILES, payload: { files: null } });
    } else {
      if (isUndefinedNull(lastTier)) {
        callGetBase();
      }
    }
  }, [tiers, lastTier]);
};

/**
 * use to Category tier
 * @param {Object} element
 * @returns
 */
export const useCategory = element => {
  const dispatch = useDispatch();
  const { action, code, tiers } = useSelector(state => state.tier);
  const { dataProductRequest } = useSelector(state => state.user);
  const { getValues } = useFormContext();
  const [categoryFields, setCategoryFields] = useState([]);
  const [tierFieldsEdit, setTierFieldEdit] = useState(null);
  const values = getValues();

  useEffect(() => {
    const dataTier = tiers[trimLowerCase(code)];
    let categories = [];
    let boolStatus = false;

    if (dataProductRequest) {
      boolStatus = dataProductRequest.some(
        item => item.status.unique_description === statusRequest.sent);
      if (boolStatus) {
        categories = dataTier.categories.fields[element.unique_name].map(cat => {
          return {
            ...cat,
            editable_after_migrate: false
          };
        });
      }
    }
    setCategoryFields(boolStatus ? categories : dataTier.categories.fields[element.unique_name]);
    dispatch({
      type: TIER_CATEGORY,
      payload: boolStatus ? categories : dataTier.categories.fields[element.unique_name]
    });
  }, [code, tiers, element, dataProductRequest]);

  useEffect(() => {
    if (action === paramsEdit && isUndefinedNull(tierFieldsEdit) && Object.keys(
      values).length > 0) {
      dispatch({ type: TIER_VALUES_EDIT, payload: values });
      setTierFieldEdit(values);
    }
  }, [action, values]);

  return { categoryFields };
};

/**
 * Custom hook used to call for the 'getProductRequest' action whenever the 'dataProductRequest' is not defined.
 */
export const useGetProductRequest = () => {
  const { data } = useSelector(state => state.personData);
  const { dataProductRequest } = useSelector(state => state.user);
  
  const dispatch = useDispatch();
  useEffect(() => {
    if (!dataProductRequest && data?.id) dispatch(getProductRequest(data.id));
  }, []);
}

/**
 * Custom hook used in the speed tier
 */
export const useSpeedTier = () => {
  const { lastTier, tier } = useSelector(state => state.tier)

  const dispatch = useDispatch()
  // ? Fixing nextTier value
  useEffect(() => {
    if (lastTier?.unique_name === 'speed_form') {
      dispatch({ type: TIER_SET_NEXT_TIER, payload: null })
    } 
  }, [lastTier])

  // ? Fixing lastTier value, for the error presented accesing 'update_data' requests
  useEffect(() => {
    if (tier?.unique_name === 'speed_form') {
      dispatch({ type: TIER_LAST_TIER, payload: tier })
    }
  }, [tier])

}